/**
 * Created by bscholl on 16.02.17.
 */
import template from './resultDiagram.tpl';
import DomUtil from '@/core/utils/domUtil';
import styles from './resultdiagram.module.scss';
import Pie from './pie';
import {gsap} from 'gsap';
import LogUtil from "@/core/utils/logUtil";


export default class ResultDiagram {
    public element: HTMLElement | null;
    public minimum: number;
    public maximum: number;
    public max_steps: number;
    public rad: number;
    public ages: any;
    public svg!: SVGElement;
    public offset!: number;
    public cx!: number;
    public cy!: number;
    public r1!: number;
    public r2!: number;
    public delta!: number;
    private _target!: HTMLElement;

    constructor(minimum = 0, maximum = 100, percent90, percent50, percent10, statisticAge, estimatedAge) {
        this.element = document.createElement('div');
        this.element.innerHTML = template({styles: styles});
        this.element = this.element.firstChild as HTMLElement;

        this.minimum = minimum;
        this.maximum = maximum;
        this.max_steps = maximum - minimum;

        this.rad = Math.PI / 180;

        this.ages = {
            percent10: percent10,
            percent50: percent50,
            percent90: percent90,
            statisticAge: statisticAge,
            estimatedAge: estimatedAge
        };
    }

    setSVGAttributes(elmt, oAtt) {
        for (let prop in oAtt) {
            elmt.setAttributeNS(null, prop, oAtt[prop]);
        }
    }

    getPointByAngleAndRadius(cx, cy, radius, a) {
        return {
            x: cx + radius * Math.cos(a * this.rad),
            y: cy + radius * Math.sin(a * this.rad)
        };
    }

    drawText(target, x, y, value, styleClass = 'scale') {
        let scaleText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        let scaleTextObj = {
            class: styleClass,
            x: x,
            y: y,
        };
        this.setSVGAttributes(scaleText, scaleTextObj);
        scaleText.textContent = value;
        target.appendChild(scaleText);

        return scaleText;
    }


    createLine(x, y, radius1, radius2, angle, styleClass = '') {

        let startPoint = this.getPointByAngleAndRadius(x, y, radius1, angle);
        let endPoint = this.getPointByAngleAndRadius(x, y, radius2, angle);

        let line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        let properties = {
            class: styleClass,
            x1: startPoint.x,
            y1: startPoint.y,
            x2: endPoint.x,
            y2: endPoint.y
        };
        this.setSVGAttributes(line, properties);
        return line;
    }

    createRect(x, y, width, height, radius, angle, styleClass = '') {
        let point = this.getPointByAngleAndRadius(x, y, radius, angle);

        let rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        let properties = {
            class: styleClass,
            x: point.x,
            y: point.y,
            width: width,
            height: height
        };
        this.setSVGAttributes(rect, properties);
        return rect
    }

    drawScale(target, centerX, centerY, start, end, startAngle, endangle, type, ticksEvery = 5, steps = 0, tickLabels = true) {
        let sr1 = this.r1;
        let sr2 = this.r2;
        let srT = this.r1 + 20;

        // if there are labels, non-labeled ticks are shorter
        let tickLength = tickLabels ? sr1 - (this.delta / 4) : sr1
        let tickLabeledLength = sr1

        let n = 0;
        let maxSteps = steps || end - start;
        for (let step = maxSteps; step >= 0; step--) {

            if (n % ticksEvery === 0 || type === 'placeholder') {

                let sa = -startAngle / maxSteps * step + endangle;
                let textPoint = this.getPointByAngleAndRadius(centerX, centerY, srT, sa);


                let tick;
                if (type === 'placeholder') {
                    if ((n === 0 || n === 60) && tickLabels) {
                        this.drawText(target, textPoint.x, textPoint.y, start + n);
                    }
                    tick = this.createLine(centerX, centerY, tickLabeledLength, sr2, sa, 'tick');
                }
                else {
                    if ((n % 15 === 0) && tickLabels) {
                        this.drawText(target, textPoint.x, textPoint.y, start + n);
                        tick = this.createLine(centerX, centerY, tickLabeledLength, sr2, sa, 'tick');
                    } else {
                        tick = this.createLine(centerX, centerY, tickLength, sr2, sa, 'tick');
                    }

                }
                target.appendChild(tick);
            }
            n++
        }

    }

    destroy() {

        if (this.element && DomUtil.isDescendant(this._target, this.element)) {
            this._target.removeChild(this.element);
        }

        this.element = null;
    }


    getAngleForAge(value, startAngle = 120, endAngle = 0, min = this.minimum, steps = this.max_steps) {
        value = parseFloat(value) - min;
        value = value * (startAngle / steps) - startAngle + endAngle;
        // prevent values that would put the angle above restriction.
        if (value > endAngle) value = endAngle;
        return value;
    }

    appendTo(target, completeCallback) {

        this._target = target;
        if (this.element) {
            this._target.appendChild(this.element);
        }

        this.svg = this._target.querySelector('svg.results_svg') as SVGElement;

        let svgWidth = 600;

        this.offset = 30;

        this.cx = ~~(svgWidth / 2);
        this.cy = ~~(svgWidth / 2);

        this.r1 = this.cx - this.offset;
        this.delta = ~~(this.r1 / 10);
        let pieRadius = this.r1 - this.delta * 0.5;
        this.r2 = pieRadius;

        let statisticalPie = new Pie(this.svg, styles.percent90, this.cx, pieRadius);
        let positivePie = new Pie(this.svg, styles.percent50, this.cx, pieRadius);
        let negativePie = new Pie(this.svg, styles.percent10, this.cx, pieRadius);

        // calculate the angles
        let estimatedAngle = 0;
        let estimatedAngleNext = 0;
        let statisticsAngle = this.getAngleForAge(this.ages.statisticAge);
        if (this.ages.estimatedAge <= 60) {
            estimatedAngle = this.getAngleForAge(this.ages.estimatedAge, 60, -120, 0, 60);
        }
        else {
            estimatedAngle = this.getAngleForAge(this.ages.estimatedAge);

        }

        // if optimist, this angle will be moved further.
        let estimatedTextAngle = estimatedAngle;
        // positive means you guessed lower than prognosis.
        let positiveEstimate = statisticsAngle > estimatedAngle;

        let p90StartAngle = 0;
        let p90EndAngle = 0;
        let p50StartAngle = 0;
        let p50EndAngle = 0;
        let p10StartAngle = 0;
        let p10EndAngle = 0;

        // draw pies depending on if estimate age is higher than statistical age
        // positive graph "pessimist" (green pie shown)
        if (positiveEstimate) {

            p90StartAngle = 0;
            p90EndAngle = estimatedAngle;

            p50StartAngle = 180 - Math.abs(estimatedAngle);
            p50EndAngle = statisticsAngle - p50StartAngle;
        }
        // negative graph "optimist" (black-lined pie shown)
        else {

            p90StartAngle = 0;
            p90EndAngle = statisticsAngle;

            p10StartAngle = 180 - Math.abs(statisticsAngle);
            p10EndAngle = estimatedAngle - p10StartAngle;

            estimatedAngleNext = estimatedAngle;
            estimatedAngle = statisticsAngle;
            estimatedTextAngle = estimatedAngleNext;

        }

        statisticalPie.startAngle = p90StartAngle; // base-pie, always on the left of indicator
        positivePie.startAngle = p50StartAngle; // this is a partial part of statisticalPie (which is reduced if used)
        negativePie.startAngle = p10StartAngle; // if estimate is higher than prognosis, this indicates the difference.

        let estimateAgeMarker = this.createLine(this.cx, this.cy, 0, this.r1, estimatedAngle, styles.estimatedMarker);
        this.svg.appendChild(estimateAgeMarker);

        let strkWdth = 6  // is 5, but the exactness has rounded pixels bleed outside
        let markerBtmMask = this.createRect(this.cx - strkWdth / 2, this.cy, (this.r1 + strkWdth / 2) * 2, strkWdth, this.r1, 180, styles.estimatedMarkerMask);
        this.svg.appendChild(markerBtmMask);

        let pos = this.getPointByAngleAndRadius(this.cx, this.cy, this.r1 + 20, estimatedTextAngle);
        let estimatedText = this.drawText(this.svg, pos.x, pos.y, this.ages.estimatedAge, styles.estimatedMarker);

        let scaleTarget = this.svg.querySelector('g.scale');
        this.drawScale(scaleTarget, this.cx, this.cy, this.minimum, this.maximum, 180, 0, 'normal', 1, 31, false);

        // --- old scale/ticks
        // first 60 items
        // this.drawScale(scaleTarget, this.cx, this.cy, 0, this.minimum - 1, 60, -120, 'placeholder');
        // real scala
        // this.drawScale(scaleTarget, this.cx, this.cy, this.minimum, this.maximum, 120, 0, 'normal');

        // overlapping text elements to hide.
        let hideMe:Element[] = DomUtil.getSVGtextElementsByProximity(this.svg, 'text.estimatedMarker', 'text.scale');

        gsap.set(estimatedText, {display: 'none', opacity: 0});
        gsap.set(estimateAgeMarker, {
            rotation: -180 - estimatedAngle,
            display: 'none',
            svgOrigin: this.cx + ' ' + this.cy
        });

        let timeline = gsap.timeline({paused: true, delay: 0.5 });
        timeline.add(gsap.to(statisticalPie, 1, {delay: 0, angle: p90EndAngle}));
        timeline.add(gsap.to(estimateAgeMarker, { duration:1,
            delay: 0,
            display: '',
            rotation: 0,
            svgOrigin: this.cx + ' ' +
                this.cy
        }),0);
        if (positiveEstimate) {

            // PESSIMIST (incl neutral exact guess)
            if (hideMe.length > 0) {
                LogUtil.log("hideMe", hideMe)
                timeline.add(gsap.to(hideMe, 0.5, {display: '', opacity: 0}));
            }
            timeline.add(gsap.to(estimatedText, 0.5, {display: '', opacity: 1}));
            timeline.add(gsap.to(positivePie, { duration:0.7, delay: 0, angle: p50EndAngle, onComplete: completeCallback}));
        } else {
            // OPTIMIST
            timeline.add(gsap.to(negativePie, {duration: 1, delay: 0.4, angle: p10EndAngle}));
            timeline.add(gsap.to(estimateAgeMarker, {
                duration: 1,
                display: '',
                rotation: estimatedAngleNext - estimatedAngle
            }),1.4);
            if (hideMe.length >0) {
                timeline.add(gsap.to(hideMe, {duration: 0.5, display: '', opacity: 0}), -0.5);
            }
            timeline.add(gsap.to(estimatedText,  {duration: 0.5, display: '', opacity: 1, onComplete: completeCallback}));
        }

        timeline.play();
    }
}
